import type { Delta as DeltaType } from 'quill/core'

import { cn } from '@nextui-org/react'
import Quill from 'quill'
import { useEffect, useRef, useState } from 'react'
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form'

import type { TextEditorInputProps } from '../type'

import { QuillEditor } from './QuillEditor'

const Delta = Quill.import('delta')

export function TextEditorInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  defaultValue,
  label,
  name,
  readonly,
  rules,
}: Readonly<TextEditorInputProps<TFieldValues, TName>>) {
  const editorRef = useRef<null | Quill>(null)
  const [editorContent, setEditorContent] = useState<DeltaType>(new Delta())

  useEffect(() => {
    if (defaultValue) {
      editorRef?.current?.setText(defaultValue)
      editorRef?.current?.clipboard.dangerouslyPasteHTML(defaultValue)
    }
  }, [defaultValue])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange }, fieldState: { error, invalid } }) => {
        function handleEditorChange(value: DeltaType) {
          setEditorContent(value)

          const htmlValue = editorRef?.current?.getSemanticHTML()
          onChange(htmlValue)
        }

        return (
          <fieldset>
            {label && (
              <label
                className={cn('mb-2 text-sm font-medium', {
                  block: true,
                  'text-danger': invalid,
                })}
                htmlFor={name}
              >
                {label}
              </label>
            )}
            <QuillEditor
              defaultValue={editorContent}
              onTextChange={handleEditorChange}
              readonly={readonly}
              ref={editorRef}
            />
            {error && (
              <span className="mt-1 block text-xs text-danger">
                {error.message}
              </span>
            )}
          </fieldset>
        )
      }}
      rules={rules}
    />
  )
}

export default TextEditorInput
